import { NoSSR } from "@sphtech/web2-core/components";
import React, { ReactElement } from "react";
import { useBodyScrollLock, useSidebarToggle, useSticky } from "../../../hooks";
import MobileBanner from "../../Block/MobileBanner/MobileBanner";
import { setMobileBannerHiddenCookie } from "../../Block/MobileBanner/mobileBannerState";
import { SocialIconProps } from "../../Block/SocialIcon/SocialIcon";
import Footer from "../../Module/Footer";
import MegaMenu from "../../Module/MegaMenu/MegaMenu";
import MegaMenuPortal from "../../Module/MegaMenu/MegaMenuPortal";
import NavbarGrid from "../../Module/NavbarGrid/NavbarGrid";
import {
  BurgerMenuIcon,
  LoginIcon,
  SearchIcon,
} from "../../Theme/assets/icons/commonIcons";
import * as Ads from "../../Utility/Ads/Ads";
import { NoAds, NoLeaderboardAd } from "../../Utility/Ads/AdsSettings";
import { RoutePathsType } from "../../Utility/dataTypes";
import styles from "./base.module.css";
import { NavigationProps } from "../../Module/NavbarGrid/types";
import SearchBar from "../../Element/SearchBar";
import cx from "classnames";
import { useBaseContext } from "../../../main";

type MegaMenuPropsType = React.ComponentProps<typeof MegaMenu>;

export type BaseLayoutProps = {
  ArticleTitle?: string;
  children: React.ReactNode;
  ads?: boolean;
  leaderboard?: boolean;
  topLeaderboard?: boolean;
  NewsTicker?: React.ReactNode;
  RoutePaths: RoutePathsType;
  svgSource: string;
  footerSvgSource?: string;
  siteName: string;
  currentLocation: {
    pathname: string;
  };
  mciText?: React.ReactNode;
  svgHamburgerMenu?: ReactElement;
  svgSearchIcon?: ReactElement;
  socialIconProps?: SocialIconProps;
  megaMenuProps: Partial<MegaMenuPropsType>;
  subscribeBoxButtonIcon?: React.ReactNode;
  skipMobileBanner?: boolean;
  NavbarComponent?: typeof NavbarGrid;
  section?: {
    name: string;
    path: string;
  };
  sphLogoInverted?: boolean;
  footerWithDivider?: boolean;
  footerWithSocialIcons?: boolean;
  NewsLetterElement?: React.ReactNode;
  navbarDropdownOptions?: NavigationProps["navbarDropdownOptions"];
  searchBoxProps?: Partial<React.ComponentProps<typeof SearchBar>>;
  currentInViewArticleRef?: HTMLElement | null;
  readProgress?: boolean;
  stickyElement?: string;
};

/**
 * Items extracted to the /Module/MegaMenu folder:
 * - MegaMenuPortal component
 * - socialMedia constants
 * - NewsLetterProps constants
 * - CloseButtonProps constants
 */
export default function BaseLayout({
  ArticleTitle,
  children,
  ads = true,
  leaderboard = true,
  topLeaderboard = false,
  NewsTicker, // <Newsticker /> passed as prop for HBS
  RoutePaths, // Routes to be provided by individual pub
  svgSource, // Logo to be provided by individual pub
  footerSvgSource, // Footer logo to be provided by individual pub
  siteName, // Used for alt text on Logo
  mciText,
  svgHamburgerMenu, // Used for different svg icon for hamburger menu
  svgSearchIcon,
  socialIconProps,
  megaMenuProps,
  subscribeBoxButtonIcon,
  skipMobileBanner: skipMobileBannerProp,
  currentLocation,
  NavbarComponent = NavbarGrid,
  section,
  sphLogoInverted = false,
  footerWithDivider = true,
  footerWithSocialIcons = true,
  NewsLetterElement,
  navbarDropdownOptions,
  searchBoxProps,
  currentInViewArticleRef,
  readProgress,
  stickyElement,
}: BaseLayoutProps): ReactElement {
  const logoSrc = `data:image/svg+xml;utf8,${encodeURIComponent(svgSource)}`;

  //used in female.
  const footerLogoSrc =
    footerSvgSource &&
    `data:image/svg+xml;utf8,${encodeURIComponent(footerSvgSource)}`;

  const [isOpen, toggleSidebar] = useSidebarToggle(false);
  const [isSearchBoxOpen, toggleSearchBox] = useSidebarToggle(false);
  const { sticky } = useSticky(stickyElement);
  useBodyScrollLock(isOpen);
  const globalData = useBaseContext();
  const { magazineIssues, navbarDropdown, userSubmissionBox } =
    globalData || {};

  const NavbarObjectProps = {
    ArticleTitle: ArticleTitle,
    toggleSidebar: toggleSidebar,
    toggleSearchPopup: toggleSearchBox,
    routes: RoutePaths.NavBar,
    LogoSource: logoSrc,
    MenuIcon: svgHamburgerMenu || BurgerMenuIcon,
    withSearch: true,
    SearchIcon: svgSearchIcon || SearchIcon,
    LoginIcon: LoginIcon,
    // Customised in CUE and passed to root data
    dropdownTeasers: navbarDropdown?.teasers,
    section,
    navbarDropdownOptions,
    readProgress,
    currentInViewArticleRef,
  };

  // overwrite text and url from magazine issues config
  const magazineIssuesButton =
    RoutePaths.NavBar.utilityLinks.find(
      (link) => link.id === "magazineIssues",
    ) ||
    RoutePaths.NavBar.primaryLinks.find((link) => link.id === "magazineIssues");
  if (magazineIssuesButton && magazineIssues?.config?.navibar) {
    const { buttonText, buttonUrl } = magazineIssues.config.navibar;
    if (buttonText) {
      magazineIssuesButton.name = buttonText;
    }
    if (buttonUrl) {
      magazineIssuesButton.url = buttonUrl;
    }
  }

  const subscribeImageCrop =
    magazineIssues?.teasers?.[0] &&
    (magazineIssues.teasers[0].image?.crops.original ||
      magazineIssues.teasers[0].image?.crops.portrait);

  const magazineIssuesMegaMenu = {
    ...magazineIssues?.config?.megamenu,
    buttonVariant: magazineIssues?.config?.megamenu?.buttonVariant || "primary",
  };
  const mobileBannerConfig = magazineIssues?.config?.mobileBanner;
  const skipMobileBanner =
    skipMobileBannerProp ||
    (Array.isArray(mobileBannerConfig?.skipPaths) &&
      mobileBannerConfig.skipPaths.includes(currentLocation.pathname));
  const cuePrimaryLinks = magazineIssues?.config?.navibar?.links;
  const cueMegaMenuLinks = magazineIssuesMegaMenu?.links;

  if (cuePrimaryLinks) {
    const primaryLinks = RoutePaths.NavBar.primaryLinks;

    cuePrimaryLinks.forEach((primaryLink) => {
      if (!primaryLinks.find((link) => link.name === primaryLink.text)) {
        primaryLinks.splice(primaryLink.position || primaryLinks.length, 0, {
          ...primaryLink,
          name: primaryLink.text, //lets do re-mapping to assign text into name
        });
      }
    });
  }

  if (cueMegaMenuLinks) {
    const megaMenuLinks = RoutePaths.MegaMenu;

    cueMegaMenuLinks.forEach((megaMenuLink) => {
      if (!megaMenuLinks.find((link) => link.text === megaMenuLink.text)) {
        megaMenuLinks.splice(
          megaMenuLink.position || megaMenuLinks.length,
          0,
          megaMenuLink,
        );
      }
    });
  }

  const noLeaderboard = !ads || !leaderboard;

  return (
    <>
      {!ads && <NoAds />}
      {noLeaderboard && <NoLeaderboardAd />}

      {!noLeaderboard && topLeaderboard && (
        <div
          className={styles.topleaderboard}
          data-layout-position="topleaderboard"
        >
          <Ads.AdsLeaderboard />
        </div>
      )}

      <header
        className={cx(
          styles.header,
          sticky && styles.sticky,
          noLeaderboard && styles.noleaderboard,
        )}
      >
        {!topLeaderboard && !sticky && NewsTicker}

        <NavbarComponent
          navObject={NavbarObjectProps}
          sticky={sticky}
          currentLocation={currentLocation}
          // Optional MEGAMENU props
          contentHubProps={navbarDropdown?.teasers}
          SubscribeBoxProps={
            magazineIssuesMegaMenu?.title && magazineIssuesMegaMenu?.buttonUrl
              ? {
                  description: magazineIssuesMegaMenu.title,
                  buttonText: magazineIssuesMegaMenu.buttonText || "SUBSCRIBE",
                  buttonIcon:
                    magazineIssuesMegaMenu.icon === true &&
                    subscribeBoxButtonIcon,
                  image: subscribeImageCrop ? subscribeImageCrop : undefined,
                  url: magazineIssuesMegaMenu.buttonUrl,
                  variant: magazineIssuesMegaMenu.buttonVariant,
                }
              : undefined
          }
          socialIconProps={socialIconProps}
          footerProps={RoutePaths.Footer}
          // Optional BANNER props
          bannerConfig={mobileBannerConfig}
        />
        <SearchBar
          isOpen={isSearchBoxOpen}
          toggleSearchBox={toggleSearchBox}
          {...searchBoxProps}
        />
      </header>

      <NoSSR>
        <MegaMenuPortal
          isOpen={isOpen}
          toggleSidebar={toggleSidebar}
          SubscribeBoxProps={
            magazineIssuesMegaMenu?.title && magazineIssuesMegaMenu?.buttonUrl
              ? {
                  description: magazineIssuesMegaMenu.title,
                  caption: magazineIssuesMegaMenu.caption,
                  buttonText: magazineIssuesMegaMenu.buttonText || "SUBSCRIBE",
                  buttonIcon:
                    magazineIssuesMegaMenu.icon === true &&
                    subscribeBoxButtonIcon,
                  image: subscribeImageCrop ? subscribeImageCrop : undefined,
                  url: magazineIssuesMegaMenu.buttonUrl,
                  variant: magazineIssuesMegaMenu.buttonVariant,
                }
              : undefined
          }
          RoutePaths={RoutePaths}
          socialIconProps={socialIconProps}
          megaMenuProps={{
            ...megaMenuProps,
            userSubmissionBox,
            ContentHubTeasers: navbarDropdown,
          }}
        />
      </NoSSR>

      {topLeaderboard && NewsTicker}

      <main>
        {mobileBannerConfig?.url && !skipMobileBanner && (
          <MobileBanner
            href={mobileBannerConfig.url}
            onClose={() => {
              setMobileBannerHiddenCookie(7, mobileBannerConfig?.cookieKey);
            }}
            dangerouslySetInnerHTML={{
              __html: mobileBannerConfig.safeHtml || "",
            }}
            contentListOpts={{
              backgroundColor: mobileBannerConfig.backgroundColor,
              color: mobileBannerConfig.color,
              showOnDesktop: mobileBannerConfig.showOnDesktop,
            }}
            image={mobileBannerConfig.image}
            imageWidth={mobileBannerConfig.imageWidth}
            imageHeight={mobileBannerConfig.imageHeight}
          />
        )}

        {ads && leaderboard && !topLeaderboard && <Ads.AdsLeaderboard />}

        {children}
      </main>
      <Footer
        NewsletterElement={NewsLetterElement}
        withDivider={footerWithDivider}
        withSocialIcons={footerWithSocialIcons}
        socialIconProps={socialIconProps}
        navLinks={RoutePaths.Footer}
        mciText={mciText}
        sphLogoInverted={sphLogoInverted}
        logoProps={{
          href: "/",
          src: footerLogoSrc || logoSrc,
          alt: siteName,
        }}
      />
    </>
  );
}
